import { App } from '@capacitor/app'
import type { PluginListenerHandle } from '@capacitor/core'
import { CapacitorHttp } from '@capacitor/core'
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import sentryConfig from 'src/store/sentry.js'
import native from 'src/config/native.js'

let appStateListener: PluginListenerHandle | undefined

const debugEnabled = import.meta.env.VITE_LIVE_UPDATE_DEBUGGER_ENABLED === 'true'

// Matches transient connectivity failures from both iOS (NSURLErrorDomain) and Android (OkHttp/Java IO).
// These should not go to Sentry — they are expected when the device is offline or the server is unreachable.
function isTransientNetworkError(error: unknown): boolean {
  const msg = String(error).toLowerCase()
  return (
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('network') ||
    msg.includes('connection') ||
    msg.includes('offline') ||
    msg.includes('internet') ||
    msg.includes('unreachable') ||
    msg.includes('econnrefused') ||
    msg.includes('unable to resolve host') ||
    msg.includes('failed to fetch')
  )
}

function isBundleExistsError(error: unknown): boolean {
  return String(error).toLowerCase().includes('bundle already exists')

}

async function checkForUpdate(): Promise<void> {
  const appUrl = import.meta.env.VITE_APP_URL

  try {
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Start checking for update, latest.json fetching from: ', `${appUrl}/bundles/latest.json`)
    const response = await CapacitorHttp.get({
      url: `${appUrl}/bundles/latest.json`,
      headers: { 'Cache-Control': 'no-store' },
      readTimeout: 10000,
      connectTimeout: 10000,
    })
    if (response.status < 200 || response.status >= 300) {
      debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Fetching latest.json failed: ', response.status)
      return
    }

    const { bundleId, url: rawUrl } = response.data
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Infos from latest.json fetched:', bundleId, rawUrl, {...response})

    const { bundleId: currentBundleId } = await LiveUpdate.getCurrentBundle()
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Infos from currently installed bundle:', currentBundleId)

    if (bundleId === currentBundleId) {
      debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Bundle is unchanged, no update available!')
      return
    }

    const { bundleIds: blockedBundleIds } = await LiveUpdate.getBlockedBundles()
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Currently blocked bundles:', blockedBundleIds)
    if (blockedBundleIds.includes(bundleId)) {
      debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Bundle is blocked (previously rolled back), skipping update:', bundleId)
      return
    }

    // Resolve relative URLs — the native HTTP client needs an absolute URL.
    const url = rawUrl.startsWith('http') ? rawUrl : `${appUrl}${rawUrl}`
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Start downloading new bundle with id and url: ', bundleId, url)

    await LiveUpdate.downloadBundle({ bundleId, url, artifactType: 'manifest' })
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Downloading new bundle has finished!')

    await LiveUpdate.setNextBundle({ bundleId })
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: Next bundleId (on next start up) is set to: ', bundleId)
  } catch (error) {
    console.log('[LiveUpdate] useLiveUpdate: Update check failed!', String(error), JSON.stringify(error))
    if (!isTransientNetworkError(error) && !isBundleExistsError(error)) {
      sentryConfig.throwError(`[LiveUpdate] Update check failed: ${String(error)}`)
    }
  }
}

export async function useLiveUpdate() {
  if (!native.isNative()) return

  const isLiveUpdateSupported = await native.isLiveUpdateSupported()
  // When live-updates via plugin are enabled, the LinkUtilsPlugin on android and iOS have no use anymore, since
  // our base URL is now https://localhost. All URLs != localhost will open outside the app automatically.
  // Plugins are checking for the preference 'liveUpdateNavigationEnabled'.
  await native.setNativePreferences('liveUpdateNavigationEnabled', String(isLiveUpdateSupported))

  if (!isLiveUpdateSupported) {
    debugEnabled && console.log('[LiveUpdate] useLiveUpdate: live updates not supported')
    return
  }
  debugEnabled && console.log('[LiveUpdate] useLiveUpdate: live updates are ENABLED')

  // Signal that the current bundle booted successfully — prevents automatic rollback.
  const readyResult = await LiveUpdate.ready()
  debugEnabled && console.log('[LiveUpdate] useLiveUpdate: LiveUpdate.ready() result:', JSON.stringify(readyResult))

  // Check for update in the background on app start (don't block app start by await)
  checkForUpdate()

  // Re-check whenever the app comes back to the foreground.
  // Remove any previous listener to prevent stacking if useLiveUpdate is called more than once.
  await appStateListener?.remove()
  appStateListener = await App.addListener('appStateChange', async ({ isActive }) => {
    if (isActive) {
      console.log('[LiveUpdate] useLiveUpdate: App was resumed from background, check for update')
      await checkForUpdate()
    }
  })
}
